import { CombatantData, RaidBuffContribution } from '../types';
import {
  addNumber,
  addPct,
  addMax,
  addHitPct,
  addOverHealPct,
  HitData,
  MaxHitData,
} from '../utils/adder';

function mergeCombatant(...args: CombatantData[]) {
  if (!args.length) {
    return null;
  }

  const dps: number[] = [];
  const rdps: number[] = [];
  const rdpsGiven: number[] = [];
  const rdpsReceived: number[] = [];
  const last10DPS: number[] = [];
  const last30DPS: number[] = [];
  const last60DPS: number[] = [];
  const hps: number[] = [];

  const swings: number[] = [];
  const hits: number[] = [];
  const deaths: number[] = [];

  const direct: HitData[] = [];
  const crit: HitData[] = [];
  const directCrit: HitData[] = [];

  const damage: number[] = [];
  const damageTaken: number[] = [];
  const damagePct: string[] = [];

  const healed: number[] = [];
  const healsTaken: number[] = [];
  const healsPct: string[] = [];
  const overHeal: number[] = [];
  const overHealPct: string[] = [];

  const maxHit: MaxHitData[] = [];
  const maxHeal: MaxHitData[] = [];

  const mergeContributions = (
    rows: RaidBuffContribution[][],
    received: boolean
  ): RaidBuffContribution[] => {
    const merged = new Map<string, RaidBuffContribution>();
    rows.flat().forEach((row) => {
      const key = received ? `${row.group}:${row.providerId}` : row.group;
      const current = merged.get(key) ?? { ...row, amount: 0, dps: 0 };
      current.amount += row.amount;
      current.dps += row.dps;
      merged.set(key, current);
    });
    return [...merged.values()].sort((a, b) => b.dps - a.dps);
  };

  args.forEach((item) => {
    dps.push(item.dps);
    rdps.push(item.rdps);
    rdpsGiven.push(item.rdpsGiven);
    rdpsReceived.push(item.rdpsReceived);
    last10DPS.push(item.last10DPS);
    last30DPS.push(item.last30DPS);
    last60DPS.push(item.last60DPS);
    hps.push(item.hps);

    swings.push(item.swings);
    hits.push(item.hits);
    deaths.push(item.deaths);

    direct.push({ hits: item.directHits, totalHits: item.hits });
    crit.push({ hits: item.critHits, totalHits: item.hits });
    directCrit.push({ hits: item.directCritHits, totalHits: item.hits });

    damage.push(item.damage);
    damageTaken.push(item.damageTaken);
    damagePct.push(item.damagePct);

    healed.push(item.healed);
    healsTaken.push(item.healsTaken);
    healsPct.push(item.healsPct);
    overHeal.push(item.overHeal);
    overHealPct.push(item.overHealPct);

    maxHit.push({ hit: item.maxHit, hitDamage: item.maxHitDamage });
    maxHeal.push({ hit: item.maxHeal, hitDamage: item.maxHealDamage });
  });

  const ret: CombatantData = {
    name: args[0].name,

    job: args[0].job,
    jobType: args[0].jobType,

    dps: addNumber(...dps),
    rdps: addNumber(...rdps),
    rdpsGiven: addNumber(...rdpsGiven),
    rdpsReceived: addNumber(...rdpsReceived),
    rdpsGivenByBuff: mergeContributions(
      args.map((item) => item.rdpsGivenByBuff),
      false
    ),
    rdpsReceivedByBuff: mergeContributions(
      args.map((item) => item.rdpsReceivedByBuff),
      true
    ),
    last10DPS: addNumber(...last10DPS),
    last30DPS: addNumber(...last30DPS),
    last60DPS: addNumber(...last60DPS),
    hps: addNumber(...hps),

    swings: addNumber(...swings),
    hits: addNumber(...hits),
    deaths: addNumber(...deaths),

    damage: addNumber(...damage),
    damageTaken: addNumber(...damageTaken),
    damagePct: addPct(...damagePct),

    healed: addNumber(...healed),
    healsTaken: addNumber(...healsTaken),
    healsPct: addPct(...healsPct),
    overHeal: addNumber(...overHeal),
    overHealPct: addOverHealPct(...overHealPct),
  } as CombatantData;

  let hitTemp = addHitPct(...direct);
  ret.directHits = hitTemp.hits;
  ret.directHitPct = hitTemp.hitPct;
  hitTemp = addHitPct(...crit);
  ret.critHits = hitTemp.hits;
  ret.critHitPct = hitTemp.hitPct;
  hitTemp = addHitPct(...directCrit);
  ret.directCritHits = hitTemp.hits;
  ret.directCritHitPct = hitTemp.hitPct;

  let maxHitTemp = addMax(...maxHit);
  ret.maxHit = maxHitTemp.hit;
  ret.maxHitDamage = maxHitTemp.hitDamage;
  maxHitTemp = addMax(...maxHeal);
  ret.maxHeal = maxHitTemp.hit;
  ret.maxHealDamage = maxHitTemp.hitDamage;

  return ret;
}

export default mergeCombatant;
